import type { PixelEvent } from '@/events';

import type { PersistentQueue } from '../queue/PersistentQueue';

export type ReplayTrigger =
  | 'init'
  | 'reload'
  | 'reconnect'
  | 'restart'
  | 'manual';

export type ReplayConfig = {
  readonly enabled: boolean;
};

export type ReplayDebugState = {
  readonly enabled: boolean;
  readonly lastTrigger: ReplayTrigger | null;
  readonly lastReplayAt: number | null;
  readonly totalReplayed: number;
  readonly pendingCount: number;
};

export class ReplayManager {
  private readonly config: ReplayConfig;

  private readonly queue: PersistentQueue;

  private lastTrigger: ReplayTrigger | null = null;

  private lastReplayAt: number | null = null;

  private totalReplayed = 0;

  private onReplay: (() => void) | null = null;

  constructor(config: ReplayConfig, queue: PersistentQueue) {
    this.config = config;
    this.queue = queue;
  }

  setReplayHandler(handler: () => void): void {
    this.onReplay = handler;
  }

  async replay(trigger: ReplayTrigger): Promise<number> {
    if (!this.config.enabled) return 0;

    await this.queue.reload();
    const count = this.queue.size();

    this.lastTrigger = trigger;
    this.lastReplayAt = Date.now();
    this.totalReplayed += count;

    if (count > 0) {
      this.onReplay?.();
    }

    return count;
  }

  getPendingEvents(): readonly PixelEvent[] {
    return this.queue.getAll();
  }

  getDebugState(): ReplayDebugState {
    return {
      enabled: this.config.enabled,
      lastTrigger: this.lastTrigger,
      lastReplayAt: this.lastReplayAt,
      totalReplayed: this.totalReplayed,
      pendingCount: this.queue.size(),
    };
  }
}
